import { observer } from 'mobx-react-lite';
import type { ShiritoriGameStore } from '@/game/core/game.store';
import { ShiritoriSquare, type PlayFeedback } from './shiritori-square';
import { determineDirection } from './direction-arrow';
import type { ShiritoriSquareState } from '@/game/core/game';

interface ShiritoriBoardProps {
    game: ShiritoriGameStore;
    onPlayFeedbackComplete?: (turnKey: string) => void;
}

export const ShiritoriBoard = observer(function ({ game, onPlayFeedbackComplete }: ShiritoriBoardProps) {
    const boardSize = game.config?.boardSize ?? 3;
    const totalCells = boardSize * boardSize;
    const isLargeBoard = boardSize === 5 || boardSize === 7;

    const hasTurns = game.turnCount > 0;
    const currentIndex = hasTurns ? game.lastTurnIndex : 0;

    const cells = Array.from({ length: totalCells }, (_, index) => {
        const turn = game.turns[index];
        let state: ShiritoriSquareState = 'empty';

        if (turn) {
            const timedOut = !!turn?.timedOutAt && !turn?.word;
            state = timedOut
                ? 'timeout'
                : (index === currentIndex) ? 'current' : 'filled';
        } else if (!hasTurns && index === currentIndex) {
            state = 'current';
        }

        return { index, turn, state };
    });

    const latestTurn = cells.find(({ index }) => index === currentIndex)?.turn;
    const latestTurnKey = latestTurn ? `${latestTurn.index}-${latestTurn.playedAt}` : undefined;
    const latestWordId = latestTurn?.word?.wordId;
    const isDuplicate = !!latestWordId && cells.some(({ index, turn }) =>
        index !== currentIndex && turn?.word?.wordId === latestWordId
    );
    const latestPlayFeedback: PlayFeedback = latestTurn?.timedOutAt ||
        (latestTurn?.word && (game.wordEndsWithN(latestTurn.word.kana) || isDuplicate))
        ? 'error'
        : 'primary';

    return (
        <div className="w-full h-full flex items-center justify-center">
            <div id="shiritori-board" className="aspect-square w-full max-w-md">
                <div className={`grid grid-cols-${boardSize} grid-rows-${boardSize} ${isLargeBoard ? 'gap-4' : 'gap-6'} w-full h-full`}>
                    {cells.map(({ index, turn, state }) => (
                        <ShiritoriSquare
                            key={index}
                            game={game}
                            word={turn?.word}
                            state={state}
                            arrow={determineDirection(index, boardSize)}
                            feedback={turn && index === currentIndex ? latestPlayFeedback : undefined}
                            onFeedbackComplete={turn && index === currentIndex && latestTurnKey
                                ? () => onPlayFeedbackComplete?.(latestTurnKey)
                                : undefined}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
});
